import React, { useState, useEffect } from 'react'
import { Search, Filter, Eye, Check, X, Download } from 'lucide-react'
import type { Contribution } from 'shared'

const Contributions: React.FC = () => {
  const [contributions, setContributions] = useState<Contribution[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterStatus, setFilterStatus] = useState<'all' | 'pending' | 'approved' | 'rejected'>('all')
  const [selectedContribution, setSelectedContribution] = useState<Contribution | null>(null)

  useEffect(() => {
    fetchContributions()
  }, [])

  const fetchContributions = async () => {
    try {
      setLoading(true)
      // 模拟数据
      const mockContributions: Contribution[] = [
        {
          id: '1',
          practitionerId: 'user1',
          imageBytes: 'base64_image_data_1',
          aiPredictionJson: JSON.stringify({
            acupoints: [
              { name: '合谷', coordinates: { x: 0.3, y: 0.4 }, confidence: 0.85 },
              { name: '足三里', coordinates: { x: 0.6, y: 0.7 }, confidence: 0.92 }
            ]
          }),
          userCorrectionJson: JSON.stringify({
            acupoints: [
              { name: '合谷', coordinates: { x: 0.32, y: 0.41 }, confidence: 1.0 },
              { name: '足三里', coordinates: { x: 0.58, y: 0.72 }, confidence: 1.0 },
              { name: '百会', coordinates: { x: 0.5, y: 0.2 }, confidence: 1.0 }
            ]
          }),
          status: 'pending',
          submittedAt: '2024-01-20T10:30:00Z'
        },
        {
          id: '2',
          practitionerId: 'user2',
          imageBytes: 'base64_image_data_2',
          aiPredictionJson: JSON.stringify({
            acupoints: [
              { name: '神门', coordinates: { x: 0.4, y: 0.5 }, confidence: 0.78 }
            ]
          }),
          userCorrectionJson: JSON.stringify({
            acupoints: [
              { name: '神门', coordinates: { x: 0.42, y: 0.52 }, confidence: 1.0 }
            ]
          }),
          status: 'approved',
          submittedAt: '2024-01-19T15:20:00Z',
          reviewedAt: '2024-01-20T09:15:00Z',
          reviewedBy: 'admin1'
        },
        {
          id: '3',
          practitionerId: 'user3',
          imageBytes: 'base64_image_data_3',
          aiPredictionJson: JSON.stringify({
            acupoints: [
              { name: '太冲', coordinates: { x: 0.7, y: 0.8 }, confidence: 0.65 }
            ]
          }),
          userCorrectionJson: JSON.stringify({
            acupoints: []
          }),
          status: 'rejected',
          submittedAt: '2024-01-18T11:45:00Z',
          reviewedAt: '2024-01-19T14:30:00Z',
          reviewedBy: 'admin1'
        }
      ]
      
      setContributions(mockContributions)
    } catch (error) {
      console.error('获取贡献数据失败:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleReview = async (contributionId: string, status: 'approved' | 'rejected') => {
    try {
      // 这里应该调用实际的API
      // await fetch(`/api/admin/contributions/${contributionId}/review`, {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify({ status, reviewedBy: 'current_admin' })
      // })
      
      // 更新本地状态
      setContributions(prev => prev.map(contrib => 
        contrib.id === contributionId 
          ? { 
              ...contrib, 
              status, 
              reviewedAt: new Date().toISOString(),
              reviewedBy: 'admin1'
            }
          : contrib
      ))
      
      setSelectedContribution(null)
    } catch (error) {
      console.error('审核失败:', error)
    }
  }

  const filteredContributions = contributions.filter(contrib => {
    const matchesSearch = contrib.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         contrib.practitionerId.toLowerCase().includes(searchTerm.toLowerCase())
    
    const matchesFilter = filterStatus === 'all' || contrib.status === filterStatus
    
    return matchesSearch && matchesFilter
  })

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800'
      case 'approved': return 'bg-green-100 text-green-800'
      case 'rejected': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getStatusText = (status: string) => {
    switch (status) {
      case 'pending': return '待审核'
      case 'approved': return '已通过'
      case 'rejected': return '已拒绝'
      default: return '未知'
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* 页面标题和操作 */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">贡献数据管理</h1>
          <p className="text-gray-500">审核和管理用户提交的穴位识别修正数据</p>
        </div>
        <div className="flex space-x-3">
          <button className="btn-secondary">
            <Download className="h-4 w-4 mr-2" />
            导出数据
          </button>
        </div>
      </div>

      {/* 搜索和筛选 */}
      <div className="card p-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="搜索贡献ID或用户ID..."
                className="input pl-10"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Filter className="h-4 w-4 text-gray-400" />
            <select
              className="input w-auto"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value as any)}
            >
              <option value="all">全部状态</option>
              <option value="pending">待审核</option>
              <option value="approved">已通过</option>
              <option value="rejected">已拒绝</option>
            </select>
          </div>
        </div>
      </div>

      {/* 贡献列表 */}
      <div className="card">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  贡献ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  用户ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  AI预测数
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  用户修正数
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  状态
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  提交时间
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  操作
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredContributions.map((contribution) => {
                const aiPrediction = JSON.parse(contribution.aiPredictionJson)
                const userCorrection = JSON.parse(contribution.userCorrectionJson)
                
                return (
                  <tr key={contribution.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {contribution.id}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{contribution.practitionerId}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{aiPrediction.acupoints?.length || 0}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{userCorrection.acupoints?.length || 0}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(contribution.status)}`}>
                        {getStatusText(contribution.status)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(contribution.submittedAt).toLocaleString('zh-CN')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => setSelectedContribution(contribution)}
                        className="text-primary-600 hover:text-primary-900 mr-3"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                      {contribution.status === 'pending' && (
                        <>
                          <button
                            onClick={() => handleReview(contribution.id, 'approved')}
                            className="text-green-600 hover:text-green-900 mr-3"
                          >
                            <Check className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleReview(contribution.id, 'rejected')}
                            className="text-red-600 hover:text-red-900"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        {filteredContributions.length === 0 && (
          <div className="text-center py-12">
            <div className="text-gray-500">
              {searchTerm || filterStatus !== 'all' ? '没有找到匹配的贡献数据' : '暂无贡献数据'}
            </div>
          </div>
        )}
      </div>

      {/* 详情模态框 */}
      {selectedContribution && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  贡献详情 - {selectedContribution.id}
                </h3>
                <button
                  onClick={() => setSelectedContribution(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="label">用户ID</label>
                  <div className="text-sm text-gray-900">{selectedContribution.practitionerId}</div>
                </div>

                <div>
                  <label className="label">提交时间</label>
                  <div className="text-sm text-gray-900">
                    {new Date(selectedContribution.submittedAt).toLocaleString('zh-CN')}
                  </div>
                </div>

                <div>
                  <label className="label">状态</label>
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(selectedContribution.status)}`}>
                    {getStatusText(selectedContribution.status)}
                  </span>
                </div>

                {selectedContribution.reviewedAt && (
                  <div>
                    <label className="label">审核时间</label>
                    <div className="text-sm text-gray-900">
                      {new Date(selectedContribution.reviewedAt).toLocaleString('zh-CN')}
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="label">AI预测结果</label>
                    <div className="bg-gray-50 p-3 rounded-md text-sm">
                      <pre className="whitespace-pre-wrap">
                        {JSON.stringify(JSON.parse(selectedContribution.aiPredictionJson), null, 2)}
                      </pre>
                    </div>
                  </div>

                  <div>
                    <label className="label">用户修正结果</label>
                    <div className="bg-gray-50 p-3 rounded-md text-sm">
                      <pre className="whitespace-pre-wrap">
                        {JSON.stringify(JSON.parse(selectedContribution.userCorrectionJson), null, 2)}
                      </pre>
                    </div>
                  </div>
                </div>

                {selectedContribution.status === 'pending' && (
                  <div className="flex justify-end space-x-3 pt-4 border-t">
                    <button
                      onClick={() => handleReview(selectedContribution.id, 'rejected')}
                      className="btn-secondary text-red-600 hover:text-red-700"
                    >
                      拒绝
                    </button>
                    <button
                      onClick={() => handleReview(selectedContribution.id, 'approved')}
                      className="btn-primary"
                    >
                      通过
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Contributions